import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/shared/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/shared/ui/dialog";
import { cn } from "@/shared/lib/utils";

/**
 * 组图轮播预览(B2-4,2026-07-31)——**唯一能验收「像不像一套」的视图**。
 *
 * ═══ 为什么必须有 ═════════════════════════════════════════════════════════
 * 组图卖的不是 6 张图,是一套能直接发的轮播。而详情页此前只有横向小图条:
 * 一次看一张、缩略图 148px 高,**版式差异恰恰在这个尺寸下看不出来**。
 * 用户要到发出去之后才发现六张不像一套。
 *
 * 这里按用户真实的观看方式还原:一次一张、左右翻、页码 1/6 —— 版式跳没跳,翻两下就知道。
 *
 * ★不叫"效果预览":画面就是成品本身,这里只是换个看法(与右回显列那条
 *   「不承诺画面」的纪律不冲突 —— 那边没有图,这边有)。
 */
export function GroupCarouselPreview({
  open,
  onOpenChange,
  items,
  startIndex = 0,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  /** 按 seq 升序的成品(url 为空 = 那张还没出/失败,占位说明) */
  items: Array<{ seq: number; url?: string | null; caption?: string; failed?: boolean }>;
  startIndex?: number;
}) {
  const [i, setI] = useState(startIndex);
  useEffect(() => {
    if (open) setI(startIndex);
  }, [open, startIndex]);

  const total = items.length;
  const cur = items[Math.min(i, Math.max(total - 1, 0))];
  const go = (d: number) => setI((v) => (total === 0 ? 0 : (v + d + total) % total));

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, total]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="text-[15px]">
            这一组翻一遍 · 第 {total === 0 ? 0 : i + 1}/{total} 张
          </DialogTitle>
        </DialogHeader>

        {/* 手机框:用户真实的观看方式(轮播一次一张,不是并排看) */}
        <div className="mx-auto w-full max-w-[300px]">
          <div className="relative overflow-hidden rounded-[18px] border-4 border-foreground/80 bg-background">
            <div className="flex min-h-[380px] items-center justify-center bg-muted/40">
              {cur?.url ? (
                <img
                  src={cur.url}
                  alt={cur.caption ?? `第 ${cur.seq} 张`}
                  className="max-h-[420px] w-full object-contain"
                />
              ) : (
                <span className="px-6 text-center text-[12.5px] leading-relaxed text-muted-foreground">
                  {cur?.failed ? "这一张没出成功" : "这一张还没出"}
                </span>
              )}
            </div>
            {/* 页点:轮播的位置感来自它 */}
            <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 gap-1.5">
              {items.map((it, n) => (
                <button
                  key={it.seq}
                  type="button"
                  aria-label={`看第 ${n + 1} 张`}
                  onClick={() => setI(n)}
                  className={cn(
                    "h-1.5 w-1.5 rounded-full transition-colors",
                    n === i ? "bg-primary" : "bg-foreground/25"
                  )}
                />
              ))}
            </div>
          </div>

          <div className="mt-2 flex items-center justify-between gap-2">
            <Button
              size="sm"
              variant="outline"
              aria-label="上一张"
              disabled={total <= 1}
              onClick={() => go(-1)}
              className="h-8 w-8 p-0"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <p className="min-w-0 flex-1 truncate text-center text-[12.5px] text-muted-foreground">
              {cur?.caption ?? ""}
            </p>
            <Button
              size="sm"
              variant="outline"
              aria-label="下一张"
              disabled={total <= 1}
              onClick={() => go(1)}
              className="h-8 w-8 p-0"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
